import { FC, useMemo, useState, useCallback } from 'react'
import styles from 'components/features/socialFeed/SocialInsightReviewActionsMenu/SocialInsightReviewActionsMenu.styl'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import cn from 'classnames'
import { ActionIcon } from 'components/features/pfm/ActionMenu/ActionIcon'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import SvgIcon from 'components/atoms/SvgIcon'
import EditSvg from 'icons/pencil.svg'
import CrossSvg from 'icons/cross.svg'
import ReportSvg from 'icons/report.svg'
import { CommentUid, PostUid } from 'interfaces/feed'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { memberFeedInfoSelector, createCommentAuthorSelector, createFeedCommentSelector } from 'selectors/feedSelectors'
import { deleteCommentActionCreator, reportCommentActionCreator } from 'actions/feedActions'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { useIsPublicPage } from 'helpers/pageHelpers'

type SocialInsightReviewActionsMenuProps = {
  commentUid: CommentUid
}
export const SocialInsightReviewActionsMenu: FC<SocialInsightReviewActionsMenuProps> = (props) => {

  const commentSelector = useMemo(createFeedCommentSelector, [])
  const comment = useStoreSelector((state) => commentSelector(state, props))

  const member = useStoreSelector(memberFeedInfoSelector)
  const commentAuthorSelector = useMemo(createCommentAuthorSelector, [])
  const author = useStoreSelector((state) => commentAuthorSelector(state, props))

  const isPublic = useIsPublicPage()

  const isOwn = useMemo(() => {
    return !isPublic && !!member && !!author && author.uid === member.uid
  }, [author, member, isPublic])

  if (!comment) {
    return null
  }

  return (
    <SocialInsightReviewActionsMenuComponent
      isOwn={ isOwn }
      isReported={ comment.is_reported }
      commentUid={ comment.uid }
      postUid={ comment.postUid }
    />
  )
}


type SocialInsightReviewActionsMenuComponentProps = {
  commentUid: CommentUid
  postUid: PostUid
  isOwn: boolean
  isReported: boolean
}
const SocialInsightReviewActionsMenuComponent: FC<SocialInsightReviewActionsMenuComponentProps> = (props) => {
  const { isOwn, isReported, commentUid, postUid } = props

  const [isShown, setIsShown] = useState(false)

  const handleShow = useCallback(() => setIsShown(true), [])
  const handleHide = useCallback(() => setIsShown(false), [])

  const dispatch = useStoreDispatch()

  const handleEdit = useCallback(() => {
    dispatch(
      modifyFloatingPaneActionCreator('opportunity-review', {
        type: 'opportunity-review',
        payload: {
          mode: 'write-review',
          uid: postUid,
          commentUid,
        },
      })
    )
  }, [dispatch, postUid, commentUid])

  const handleDelete = useCallback(() => {
    dispatch(deleteCommentActionCreator.request({
      uid: commentUid,
      postUid,
    }))
  }, [dispatch, commentUid, postUid])

  const handleReport = useCallback(() => {
    dispatch(reportCommentActionCreator.request({
      uid: commentUid,
    }))
    handleHide()
  }, [dispatch, commentUid, handleHide])

  return (
    <div className={ styles.socialInsightReviewActionsMenu }>
      <div className={ styles.actionMenuContainer }>
        <button
          className={ styles.actionMenuButton }
          onClick={ handleShow }
        >
          <ActionIcon />
        </button>

        <ActionMenu
          className={ cn(styles.actionMenu, isShown && styles.actionMenuShown) }
          onHide={ handleHide }
        >
          { isOwn && (
            <>
              <AnalyticsClick
                category='module-feed_post_comment'
                action='click-edit'
              >
                <ActionMenuItem
                  icon={ <SvgIcon src={ EditSvg } /> }
                  onClick={ handleEdit }
                >
                  Edit
                </ActionMenuItem>
              </AnalyticsClick>
              <AnalyticsClick
                category='module-feed_post_comment'
                action='click-delete'
              >
                <ActionMenuItem
                  icon={ <SvgIcon src={ CrossSvg } /> }
                  onClick={ handleDelete }
                >
                  Delete
                </ActionMenuItem>
              </AnalyticsClick>
            </>
          )}
          { !isOwn && (
            <AnalyticsClick
              category='module-feed_post_comment'
              action='toggle-report'
              label={ isReported ? 'unreport' : 'report' }
            >

              <ActionMenuItem
                icon={ <SvgIcon src={ ReportSvg } /> }
                onClick={ handleReport }
                isDisabled={ isReported }
              >
                { isReported ? 'Reported' : 'Report' }
              </ActionMenuItem>
            </AnalyticsClick>
          )}
        </ActionMenu>
      </div>
    </div>
  )
}
